import { useEffect, useState } from 'react';
import { auth } from '../lib/firebase';
import {
  setOnline,
  setOffline,
  updateLastActive,
  setTyping,
  subscribePresence,
  type Presence,
} from '../services/presence';

/**
 * Subscribe to another user's presence.
 * Pass a `userId` (string). Returns `{ presence }` where presence can be null
 * if the user has never been seen before.
 */
export function usePresence(userId: string | null) {
  const [presence, setPresence] = useState<Presence | null>(null);

  useEffect(() => {
    if (!userId) {
      setPresence(null);
      return;
    }
    const unsub = subscribePresence(userId, (p) => setPresence(p));
    return () => unsub();
  }, [userId]);

  return { presence };
}

/**
 * Maintain current user's presence lifecycle.
 * - Sets online on mount
 * - Sets offline on unmount
 * - Updates lastActive periodically
 * - Provides helpers to mark typing state
 */
export function useSelfPresence(options?: { heartbeatMs?: number }) {
  const heartbeatMs = options?.heartbeatMs ?? 30000; // 30s heartbeat

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    (async () => {
      await setOnline();
      interval = setInterval(() => {
        updateLastActive().catch(() => {});
      }, heartbeatMs);
    })();

    const onVisibilityChange = () => {
      if (document.hidden) {
        setOffline().catch(() => {});
      } else {
        setOnline().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (interval) clearInterval(interval);
      setOffline().catch(() => {});
    };
  }, [heartbeatMs]);

  // Convenience function to set typing scoped to a conversation id
  const setTypingState = async (conversationId: string | null, isTyping: boolean) => {
    if (!auth.currentUser) return;
    try {
      await setTyping(conversationId, isTyping);
    } catch {}
  };

  return { setTypingState };
}